import AppIconButton from "../../../components/form/AppIconButton";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Chip, Tab, Tabs, Tooltip } from "@mui/material";
import Icon from "@mui/material/Icon";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import AppsOutlinedIcon from "@mui/icons-material/AppsOutlined";
import PendingActionsOutlinedIcon from "@mui/icons-material/PendingActionsOutlined";
import { useTranslation } from "react-i18next";
import { PageHeader, PageListContainer } from "../../../components/Page";
import PackageLimitHint from "../../../components/PackageLimitHint";
import { EditorSlideOver, PageSectionCard } from "../../../components/layout";
import { AppButton, AppFileButton } from "../../../components/form";
import DataTableWithPagination from "../../../components/DataTable";
import { widgetService } from "../../../services/widgetService";
import { adminWidgetService } from "../../../services/adminWidgetService";
import { getCurrentUserRoles, isWorkspaceAdmin } from "../../../utils/authorization";
import { formatLimitValue, usePackageLimits } from "../../../hooks/usePackageLimits";
import { useAppCustomStyles } from "../../../assets/theme/custom/hooks";
import WidgetEditPage from "./WidgetEditPage";

type FilterTab = "all" | "pending";
type EditorTarget = { mode: "create" } | { mode: "edit"; id: string };

export default function WidgetsPage() {
  const { t } = useTranslation();
  const custom = useAppCustomStyles();
  const tableRef = useRef<any>(null);
  const [tab, setTab] = useState<FilterTab>("all");
  const [userIsAdmin, setUserIsAdmin] = useState(false);
  const [editor, setEditor] = useState<EditorTarget | null>(null);
  const packageLimits = usePackageLimits();

  useEffect(() => {
    getCurrentUserRoles().then((roles) => setUserIsAdmin(isWorkspaceAdmin(roles)));
  }, []);

  const fetchUrl =
    tab === "pending" ? "/widgets?rsql=status==PENDING_INDEX_APPROVAL" : "/widgets";

  const refreshTable = useCallback(() => {
    tableRef.current?.refresh();
  }, []);

  const closeEditor = useCallback(() => setEditor(null), []);

  const handleEdit = useCallback((row: { id?: string }) => {
    if (row.id) setEditor({ mode: "edit", id: row.id });
  }, []);

  const handleDelete = useCallback(
    async (row: { id?: string }) => {
      if (!row.id) return;
      await widgetService.remove(row.id);
    },
    []
  );

  const handleDownload = useCallback(async (row: { id?: string }) => {
    if (!row.id) return;
    const w = await widgetService.get(row.id);
    widgetService.exportJson(w);
  }, []);

  const cols = useMemo(() => {
    const base = [
      {
        name: t("widget.name"),
        selector: (row: { name?: string }) => row.name || "",
        sortable: true,
      },
      {
        name: t("widget.status"),
        selector: (row: { status?: string }) => row.status || "ACTIVE",
        cell: (row: { status?: string }) =>
          row.status === "PENDING_INDEX_APPROVAL" ? (
            <Chip size="small" color="warning" label={t("widget.pendingApprovalShort")} />
          ) : (
            row.status || "ACTIVE"
          ),
        sortable: true,
      },
      {
        name: "v",
        selector: (row: { metadata?: { version?: number } }) => row.metadata?.version ?? 1,
        sortable: false,
        width: "64px",
      },
    ];

    if (!userIsAdmin) return base;

    return [
      ...base,
      {
        name: t("widget.approveIndex"),
        cell: (row: { id?: string; status?: string }) => {
          if (row.status !== "PENDING_INDEX_APPROVAL" || !row.id) return null;
          return (
            <Box sx={custom.common.flexRowGapHalf}>
              <Tooltip title={t("widget.approveIndex")}>
                <AppIconButton
                  size="small"
                  color="success"
                  onClick={async (e) => {
                    e.stopPropagation();
                    await adminWidgetService.approveIndex(row.id!);
                    window.dispatchEvent(
                      new CustomEvent("showGlobalNotification", {
                        detail: {
                          color: "success",
                          icon: "check",
                          title: t("widget.approveIndex"),
                          content: t("widget.indexApproved"),
                        },
                      })
                    );
                    refreshTable();
                  }}
                >
                  <Icon fontSize="small">check_circle</Icon>
                </AppIconButton>
              </Tooltip>
              <Tooltip title={t("widget.rejectIndex")}>
                <AppIconButton
                  size="small"
                  color="warning"
                  onClick={async (e) => {
                    e.stopPropagation();
                    await adminWidgetService.rejectIndex(row.id!);
                    refreshTable();
                  }}
                >
                  <Icon fontSize="small">block</Icon>
                </AppIconButton>
              </Tooltip>
            </Box>
          );
        },
        sortable: false,
        width: "100px",
      },
    ];
  }, [t, userIsAdmin, refreshTable]);

  const onImport = async (file: File) => {
    await widgetService.importJson(file);
    refreshTable();
  };

  return (
    <PageListContainer>
      <PageHeader
        title={t("widget.title")}
        actions={
          <Box>
            <AppButton
              variant="contained"
              startIcon={<AddOutlinedIcon />}
              onClick={() => setEditor({ mode: "create" })}
              sx={custom.common.marginRightOne}
            >
              {t("widget.create")}
            </AppButton>
            <AppFileButton variant="outlined" accept="application/json" onFileSelected={onImport}>
              {t("common.import")}
            </AppFileButton>
          </Box>
        }
      />
      {packageLimits && (
        <PackageLimitHint group="count" capped={packageLimits.maxWidgets != null}>
          {t("packageLimits.widgets", {
            count: formatLimitValue(packageLimits.maxWidgets, t("packageLimits.unlimited")),
          })}
        </PackageLimitHint>
      )}
      {userIsAdmin && (
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={custom.common.tabsMarginBottomTwo}>
          <Tab
            value="all"
            icon={<AppsOutlinedIcon fontSize="small" />}
            iconPosition="start"
            label={t("widget.tabAll")}
          />
          <Tab
            value="pending"
            icon={<PendingActionsOutlinedIcon fontSize="small" />}
            iconPosition="start"
            label={t("widget.tabPending")}
          />
        </Tabs>
      )}
      <PageSectionCard title={t("widget.listTitle")} noPadding>
        <DataTableWithPagination
          key={fetchUrl}
          ref={tableRef}
          cols={cols}
          fetchUrl={fetchUrl}
          handleEdit={handleEdit}
          handleDelete={handleDelete}
          handleDownload={handleDownload}
          searchKey="name"
          searchCollapseToIcon
        />
      </PageSectionCard>

      <EditorSlideOver
        open={Boolean(editor)}
        onClose={closeEditor}
        title={editor?.mode === "create" ? t("widget.create") : t("widget.edit")}
      >
        {editor?.mode === "create" ? (
          <WidgetEditPage mode="create" embedded onClose={closeEditor} onSaved={refreshTable} />
        ) : editor?.mode === "edit" ? (
          <WidgetEditPage
            mode="edit"
            embedded
            widgetId={editor.id}
            onClose={closeEditor}
            onSaved={refreshTable}
          />
        ) : null}
      </EditorSlideOver>
    </PageListContainer>
  );
}
